// cluster/components/form/hooks/useTariffForm.ts
import { useState, useCallback } from 'react'
import { useValidation } from '../../../../../common/hooks/useValidation'
import {
  TariffRule,
  ValidationRulesInterface,
  ValidationResultInterface,
} from '../../../../../common/types/interfaces'
import { format, addMonths, startOfDay, endOfDay } from 'date-fns'

const validations: ValidationRulesInterface[] = [
  {
    fieldName: 'name',
    canBeEmpty: false,
    isEmptyMessage: 'Введите название тарифа',
    toShortMessage: '',
    tooLongMessage: '',
    formatMessage: '',
  },
  {
    fieldName: 'basePrice',
    canBeEmpty: false,
    isEmptyMessage: 'Введите базовую цену',
    toShortMessage: '',
    tooLongMessage: '',
    formatMessage: '',
  },
  {
    fieldName: 'specialPrice',
    canBeEmpty: true,
    isEmptyMessage: '',
    toShortMessage: '',
    tooLongMessage: '',
    formatMessage: '',
  },
]

type Props = {
  tariff: TariffRule | undefined
  setTariff: (data: TariffRule) => void
}

export const useTariffForm = ({ tariff, setTariff }: Props) => {
  const [validationResult, setValidationResult] = useState<ValidationResultInterface[]>([])
  const [settingsDisabled, setSettingsDisabled] = useState(false)

  const { validate, checkAndAddValidation, setDirty } = useValidation(
    setValidationResult,
    validationResult,
    validations,
  )

  const inputHandler = useCallback(
    (
      value: string | number | boolean | (string | undefined)[],
      fieldName: string,
      type: string,
    ) => {
      const newValue =
        type === 'number' || type === 'numberText' ? (value ? Number(value) : null) : value
      const newTariff = { ...tariff, [fieldName]: newValue } as TariffRule
      setTariff(newTariff)
      validate(fieldName, newValue)
    },
    [tariff, setTariff, validate],
  )

  const toggleAction = useCallback(
    (fieldName: string) => {
      const newValue = !tariff?.[fieldName]
      const newTariff = { ...tariff, [fieldName]: newValue } as TariffRule

      if (
        fieldName === 'datesImportant' &&
        newValue &&
        !newTariff.validFrom &&
        !newTariff.validTo
      ) {
        const now = new Date()
        const threeMonthsLater = addMonths(now, 3)
        newTariff.validFrom = format(startOfDay(now), "yyyy-MM-dd'T'HH:mm:ss")
        newTariff.validTo = format(endOfDay(threeMonthsLater), "yyyy-MM-dd'T'HH:mm:ss")
      }

      setTariff(newTariff)
      validate(fieldName, newValue)
    },
    [tariff, setTariff, validate],
  )

  const handleDateChange = useCallback(
    (value: string, isFrom: boolean) => {
      const date = new Date(value)
      const adjustedDate = isFrom ? startOfDay(date) : endOfDay(date)
      const formattedDate = format(adjustedDate, "yyyy-MM-dd'T'HH:mm:ss")
      if (isFrom) {
        setTariff({ ...tariff, validFrom: formattedDate } as TariffRule)
      } else {
        setTariff({ ...tariff, validTo: formattedDate } as TariffRule)
      }
    },
    [tariff, setTariff],
  )

  const setDurationPreset = useCallback(
    (months: number) => {
      const now = new Date()
      const from = startOfDay(now)
      const to = endOfDay(addMonths(now, months))
      setTariff({
        ...tariff,
        validFrom: format(from, "yyyy-MM-dd'T'HH:mm:ss"),
        validTo: format(to, "yyyy-MM-dd'T'HH:mm:ss"),
        datesImportant: true,
      } as TariffRule)
    },
    [tariff, setTariff],
  )

  const getValidationResult = (fieldName: string) => {
    return validationResult.find((el) => el.name === fieldName)
  }

  return {
    inputHandler,
    toggleAction,
    setSettingsDisabled,
    getValidationResult,
    setDirty,
    handleDateChange,
    setDurationPreset,
  }
}
